import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit
} from '@angular/fire/firestore';
import { Observable, from, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Video, VideoProgress, VideoMetadata } from '../models/video.model';
import { CacheService } from './cache.service';
import { NetworkService } from './network.service';

@Injectable({ providedIn: 'root' })
export class VideoService {
  private firestore = inject(Firestore);
  private cacheService = inject(CacheService);
  private networkService = inject(NetworkService);
  private injector = inject(Injector);
  private videosCollection = collection(this.firestore, 'videos');

  getVideosByLeaderId(leaderId: string): Observable<Video[]> {
    const q = query(
      this.videosCollection,
      where('leaderId', '==', leaderId),
      orderBy('orderSequence')
    );
    return from(runInInjectionContext(this.injector, () => getDocs(q))).pipe(
      map(snap => snap.docs.map(d => ({ id: d.id, ...d.data() } as Video)))
    );
  }

  getVideosByIds(videoIds: string[]): Observable<Video[]> {
    if (!videoIds.length) return of([]);
    return from(
      runInInjectionContext(this.injector, () =>
        Promise.all(videoIds.map(id => getDoc(doc(this.firestore, `videos/${id}`))))
      )
    ).pipe(
      map(snaps =>
        snaps
          .filter(s => s.exists())
          .map(s => ({ id: s.id, ...s.data() } as Video))
          .sort((a, b) => (a.orderSequence ?? 0) - (b.orderSequence ?? 0))
      )
    );
  }

  getVideoById(videoId: string): Observable<Video | null> {
    const docRef = doc(this.firestore, `videos/${videoId}`);
    return from(runInInjectionContext(this.injector, () => getDoc(docRef))).pipe(
      map(snap => snap.exists() ? ({ id: snap.id, ...snap.data() } as Video) : null)
    );
  }

  getRecentVideos(maxCount = 20): Observable<Video[]> {
    const q = query(
      this.videosCollection,
      orderBy('orderSequence'),
      limit(maxCount)
    );
    return from(runInInjectionContext(this.injector, () => getDocs(q))).pipe(
      map(snap => snap.docs.map(d => ({ id: d.id, ...d.data() } as Video))),
      catchError(() => of([]))
    );
  }

  getFeaturedVideos(): Observable<Video[]> {
    const q = query(
      this.videosCollection,
      where('isFeatured', '==', true),
      limit(5)
    );
    return from(runInInjectionContext(this.injector, () => getDocs(q))).pipe(
      map(snap => snap.docs.map(d => ({ id: d.id, ...d.data() } as Video)))
    );
  }

  getRecentlyWatchedVideos(userId: string): Observable<VideoProgress[]> {
    const q = query(
      collection(this.firestore, `users/${userId}/videoProgress`),
      orderBy('lastWatchedAt', 'desc'),
      limit(10)
    );
    return from(runInInjectionContext(this.injector, () => getDocs(q))).pipe(
      map(snap => snap.docs.map(d => d.data() as VideoProgress)),
      catchError(() => of([]))
    );
  }

  /**
   * Offline-first: saves locally immediately, syncs to Firestore when online.
   */
  async updateVideoProgress(userId: string, videoId: string, currentTime: number, duration: number): Promise<void> {
    const completed = duration > 0 && (currentTime / duration) >= 0.95;
    const percentage = duration > 0 ? (currentTime / duration) * 100 : 0;

    // Always persist locally
    await this.cacheService.saveVideoProgress(userId, videoId, {
      videoId, currentTime, duration, percentage
    });

    const syncFn = async () => {
      const progressRef = doc(this.firestore, `users/${userId}/videoProgress/${videoId}`);
      await setDoc(progressRef, {
        videoId, userId, currentTime, duration, completed,
        lastWatchedAt: new Date()
      }, { merge: true });
      await this.cacheService.markProgressSynced(userId, videoId);
    };

    this.networkService.queueForSync(syncFn);
  }

  async getVideoProgress(userId: string, videoId: string): Promise<VideoProgress | null> {
    // Prefer Firestore — it contains all fields including completedChapters.
    // Cache is used only when offline.
    if (this.networkService.isOnline) {
      try {
        const progressRef = doc(this.firestore, `users/${userId}/videoProgress/${videoId}`);
        const snap = await runInInjectionContext(this.injector, () => getDoc(progressRef));
        if (snap.exists()) return snap.data() as VideoProgress;
      } catch {
        // fall through to cache on network error
      }
    }

    const cached = await this.cacheService.getVideoProgress(userId, videoId);
    return cached ? (cached as unknown as VideoProgress) : null;
  }

  /**
   * Sync all unsynced local progress to Firestore (call on app resume / network restored).
   */
  async syncOfflineProgress(userId: string): Promise<void> {
    const unsynced = await this.cacheService.getUnsyncedProgress(userId);
    for (const [videoId, progress] of Object.entries(unsynced)) {
      try {
        const progressRef = doc(this.firestore, `users/${userId}/videoProgress/${videoId}`);
        await setDoc(progressRef, {
          videoId, userId,
          currentTime: progress.currentTime,
          duration: progress.duration,
          completed: progress.duration > 0 && (progress.currentTime / progress.duration) >= 0.95,
          lastWatchedAt: new Date(progress.updatedAt)
        }, { merge: true });
        await this.cacheService.markProgressSynced(userId, videoId);
      } catch (e) {
        console.warn(`Failed to sync progress for video ${videoId}:`, e);
      }
    }
  }

  async markChapterCompleted(userId: string, videoId: string, completedChapters: number[]): Promise<void> {
    const progressRef = doc(this.firestore, `users/${userId}/videoProgress/${videoId}`);
    await setDoc(progressRef, { completedChapters }, { merge: true });
  }

  async markVideoAsComplete(userId: string, videoId: string): Promise<void> {
    const progressRef = doc(this.firestore, `users/${userId}/videoProgress/${videoId}`);
    await setDoc(progressRef, { videoId, userId, completed: true }, { merge: true });

    const userRef = doc(this.firestore, `users/${userId}`);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const data = userSnap.data();
      const completedVideos: string[] = data['progress']?.completedVideos || [];
      if (!completedVideos.includes(videoId)) {
        await updateDoc(userRef, {
          'progress.completedVideos': [...completedVideos, videoId]
        });
      }
    }
  }
}
